import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../hooks/useAuth';
import { useLabelResolver } from '../../hooks/useLabelResolver';
import { useRoleNameById } from '../../hooks/useRoleNameById';
import { tenantApi } from '../../api/endpoints/tenant';
import { platformApi } from '../../api/endpoints/platform';
import { isPlatformPoolTenant, resolvePlatformContainerTenantId } from '../../lib/brandingTenant';
import type { Permission } from '../../api/types/permissions';
import { Badge } from '../ui/badge';

const UNNAMED_ROLE = 'Rol asignado';
const UNNAMED_TENANT = 'tu organización';

interface RolePermissions {
    roleId: string;
    permissions: Permission[];
}

export function MyRoleAssignments() {
    const { roleAssignments, user } = useAuth();
    const labels = useLabelResolver();
    const roleNameById = useRoleNameById();
    // El pool de plataforma tiene sus roles en /platform/roles; el resto, en los de su organización.
    const isPlatform = isPlatformPoolTenant(user?.tenantId);
    const tid = resolvePlatformContainerTenantId(user?.tenantId);

    // Un mismo rol puede estar asignado en varios scopes; sin mostrar scope, deduplicamos por roleId.
    const uniqueRoles = Array.from(new Map(roleAssignments.map((a) => [a.roleId, a])).values());

    // Misma queryKey y queryFn que useRoleNameById: el caché guarda la respuesta cruda y `select` la adapta.
    const platformRolesQ = useQuery({
        queryKey: ['platform', 'roles'],
        queryFn: () => platformApi.listPlatformRoles(),
        select: (res): RolePermissions[] =>
            res.items.map((r) => ({ roleId: r.roleId, permissions: r.permissions ?? [] })),
        enabled: isPlatform,
    });
    const tenantRolesQ = useQuery({
        queryKey: ['tenant', tid, 'roles'],
        queryFn: () => tenantApi.listRoles(tid ?? ''),
        select: (res): RolePermissions[] =>
            res.items.map((r) => ({ roleId: r.roleId, permissions: r.permissions ?? [] })),
        enabled: !isPlatform && !!tid,
    });
    const roleItems = (isPlatform ? platformRolesQ.data : tenantRolesQ.data) ?? [];
    const permsByRole = new Map(roleItems.map((r) => [r.roleId, r.permissions]));

    const [open, setOpen] = useState<Set<string>>(new Set());
    const toggle = (roleId: string): void => {
        setOpen((prev) => {
            const next = new Set(prev);
            if (next.has(roleId)) next.delete(roleId);
            else next.add(roleId);
            return next;
        });
    };

    if (uniqueRoles.length === 0) {
        const tenantLabel = labels.scope('TENANT', user?.tenantId);
        const tenantName = tenantLabel === user?.tenantId ? UNNAMED_TENANT : tenantLabel;
        return (
            <p className="text-muted-foreground">
                {`Aún no tienes un rol asignado en ${tenantName}. Pide a tu administrador que te asigne uno.`}
            </p>
        );
    }

    return (
        <div className="space-y-2">
            {uniqueRoles.map((a) => {
                const perms = permsByRole.get(a.roleId) ?? [];
                const canExpand = perms.length > 0;
                const isOpen = open.has(a.roleId);
                return (
                    <div key={a.roleId} className="rounded-lg border border-border">
                        <button
                            type="button"
                            disabled={!canExpand}
                            onClick={() => {
                                toggle(a.roleId);
                            }}
                            className="flex w-full items-center gap-2 p-3 text-left disabled:cursor-default"
                        >
                            <ShieldCheck className="size-4 shrink-0 text-brand-accent" />
                            <span className="font-medium">
                                {a.roleName?.trim() || roleNameById.get(a.roleId) || UNNAMED_ROLE}
                            </span>
                            {canExpand && (
                                <span className="ml-auto flex items-center gap-1 text-xs text-muted-foreground">
                                    {perms.length} {perms.length === 1 ? 'permiso' : 'permisos'}
                                    {isOpen ? (
                                        <ChevronDown className="size-4" />
                                    ) : (
                                        <ChevronRight className="size-4" />
                                    )}
                                </span>
                            )}
                        </button>
                        {canExpand && isOpen && (
                            <div className="flex flex-wrap gap-1.5 border-t border-border p-3">
                                {perms.map((p) => {
                                    const { name, description } = labels.permission(p);
                                    return (
                                        <Badge
                                            key={p}
                                            variant="secondary"
                                            className="font-normal"
                                            title={description}
                                        >
                                            {name}
                                        </Badge>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                );
            })}
        </div>
    );
}
